import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import FormSectionSubtitle from "@rentiohq/shared-frontend/dist/components/components/FormSectionSubtitle";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import ListItemSelect from "@rentiohq/shared-frontend/dist/components/components/ListItemSelect";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import { PaymentRequestFetch } from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import { PropertyFetchListItem } from "@rentiohq/shared-frontend/dist/components/components/PropertyFetchListItem";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { VopStatusBadge } from "@rentiohq/shared-frontend/dist/components/components/VopStatusBadge";
import logger from "@rentiohq/shared-frontend/dist/logger";
import { logError } from "@rentiohq/shared-frontend/dist/logger/logger";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as bankAccountActions from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.actions";
import * as bankAccountApi from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.api";
import * as bankAccountSelectors from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.selectors";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import { IIban } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { IPaymentRequest } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { usePrevious } from "@rentiohq/shared-frontend/dist/utils/hooks.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import withKycRequired from "hoc/withKycRequired";
import queryString from "query-string";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { IRootState } from "redux/reducers";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import PaymentRequestRedirect from "../../components/PaymentRequestRedirect";
import * as infoUtils from "../../utils/info.utils";
import * as S from "./MangopayOverview.styles";

let autoSubmitInterval: NodeJS.Timeout | undefined;

const MangopayOverviewBase = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const user = useSelector((state: IRootState) => authSelectors.getUser(state));
  const personalContactId = user?.personalContactId;
  const ibans = useSelector(
    (state: IRootState) =>
      bankAccountSelectors.getContactBankAccounts(state, {
        contactId: personalContactId,
      }) || [],
  );
  const isFetchingIbans = useSelector((state: IRootState) =>
    bankAccountSelectors.isFetchingContactBankAccounts(state, {
      contactId: personalContactId,
    }),
  );
  const wasFetchingIbans = usePrevious(isFetchingIbans);
  const fetchIbanError = useSelector((state: IRootState) =>
    bankAccountSelectors.contactBankAccountsFetchError(state, {
      contactId: personalContactId,
    }),
  );

  const { ibanId: ibanIdRaw } = queryString.parse(location.search) as {
    ibanId?: string;
  };
  const ibanId = ibanIdRaw ? parseInt(ibanIdRaw, 0) : undefined;

  const [selectedIbanId, setSelectedIbanId] = React.useState(ibanId);
  const [pendingPressPay, setPendingPressPay] = React.useState(false);

  // Data
  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const fetchData = () => {
    if (!user) {
      logError({
        error: new Error("Could not fetch IBAN's. No user."),
      });
      return;
    }

    dispatch(
      bankAccountActions.getContactBankAccountsStart.getAction({
        contactId: user.personalContactId,
      }),
    );
  };

  const fetchIban = async (id: number) => {
    if (!user) {
      return;
    }

    const { data: ibans } = await bankAccountApi.getBankAccountsForContact(
      user.personalContactId,
    );
    return ibans.find(randomIban => id === randomIban.id);
  };

  // Lifecycle
  React.useEffect(() => {
    fetchData();
    handleAutoSubmit();

    return () => {
      if (autoSubmitInterval) {
        clearInterval(autoSubmitInterval);
      }
    };
  }, []);

  React.useEffect(() => {
    const stoppedFetchingIbans = !isFetchingIbans && wasFetchingIbans;

    if (stoppedFetchingIbans && !fetchIbanError && ibans.length === 0) {
      handlePressAddIban();
    }
  }, [isFetchingIbans, fetchIbanError, ibans]);

  // Event handlers
  const handlePressMoreInfo = () => {
    const url = getLocalizedText(
      "payment.online.select_method.cta.info.url.sepa",
    );
    window.open(url, "_blank");
  };

  const handlePressIban = (iban: IIban) => () => {
    setSelectedIbanId(iban.id);
  };

  const handlePressAddIban = () => {
    if (!paymentRequestId) {
      return;
    }

    navigate(`/payment/${paymentRequestId}/payment-method/mangopay/add-iban`, {
      state: { referrer: location.pathname + location.search },
    });
  };

  const handleAutoSubmit = async () => {
    if (!isAutoSubmit()) {
      return;
    }

    if (!selectedIbanId) {
      return;
    }

    setPendingPressPay(true);

    try {
      const iban = await fetchIban(selectedIbanId);
      if (!iban) {
        return;
      }

      if (!iban.mandateApprovedAt) {
        startAutoSubmitInterval();
        return;
      }

      clearAutoSubmitInterval();
      handlePressPay();
    } catch (unknownError) {
      const error = unknownError as any;

      logger.logError({ error });
    }
  };

  const handlePressPay = async () => {
    if (!paymentRequestId) {
      return;
    }

    if (!selectedIbanId) {
      return;
    }

    setPendingPressPay(true);

    try {
      // Fetch IBAN (to get latest info)
      const iban = await fetchIban(selectedIbanId);
      if (!iban) {
        throw new Error("No IBAN");
      }

      // Request mandate
      if (!iban.mandateApprovedAt) {
        const { data: mandateRequest } = await paymentRequestApi.getMandate(
          selectedIbanId,
          getMandateCallbackUrl(selectedIbanId),
        );

        const redirectUrl = mandateRequest.RedirectUrl;

        window.open(redirectUrl, "_self");

        return;
      }
    } catch (unknownError) {
      setPendingPressPay(false);

      return;
    }

    try {
      // Pay
      await paymentRequestApi.payPaymentRequest(
        paymentRequestId,
        selectedIbanId,
        isRecurring(),
      );

      // Redirect to status page after success or error
      navigate(`/payment/${paymentRequestId}/status`);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });
      showAlert({
        type: "error",
        message: error.message || "Something went wrong",
      });
    }

    setPendingPressPay(false);
  };

  // Helpers
  const isRecurring = () => {
    const { type = "once" } = queryString.parse(location.search) as {
      type?: string;
    };
    return type === "recurring";
  };

  const isAutoSubmit = () => {
    const { autoSubmit } = queryString.parse(location.search) as {
      autoSubmit?: string;
    };

    return autoSubmit === "1";
  };

  const getMandateCallbackUrl = (ibanId: number) => {
    const result = queryString.parseUrl(window.location.href);
    result.query.ibanId = `${ibanId}`;
    result.query.autoSubmit = "1";
    return queryString.stringifyUrl(result);
  };

  const startAutoSubmitInterval = () => {
    if (autoSubmitInterval) {
      return;
    }

    autoSubmitInterval = setInterval(handleAutoSubmit, 1000);
  };

  const clearAutoSubmitInterval = () => {
    if (!autoSubmitInterval) {
      return;
    }

    clearInterval(autoSubmitInterval);
    autoSubmitInterval = undefined;
  };

  // Render
  const renderPaymentRequestAndProperty = (
    paymentRequest: IPaymentRequest,
    property?: IProperty,
  ) => {
    if (ibans.length === 0 && isFetchingIbans) {
      return (
        <Page withScroll={false}>
          <FullScreenActivityIndicator />
        </Page>
      );
    }

    return (
      <Page>
        <InfoHeader
          title={infoUtils.getTitle({
            postfix: "mangopay_overview",
            paymentRequest,
            property,
          })}
          body={infoUtils.getBody({
            postfix: "mangopay_overview",
            paymentRequest,
            property,
          })}
          buttonTitle={getLocalizedText(
            "payment.online.select_iban.cta.info.title",
          )}
          onPressButton={handlePressMoreInfo}
        />
        <S.LisWrap>
          {renderIbans()}

          <Spacer weight={ESpacerWeight.W24} />

          <S.ButtonPrimaryWrap>
            <ButtonPrimary
              title={getLocalizedText("payment_detail.cta.pay")}
              isDisabled={!selectedIbanId}
              isLoading={pendingPressPay}
              onPress={handlePressPay}
              buttonSize={EButtonSize.Full}
            />
          </S.ButtonPrimaryWrap>
        </S.LisWrap>
      </Page>
    );
  };

  const renderPaymentRequest = (paymentRequest: IPaymentRequest) => (
    <PropertyFetchListItem
      shouldRefetch={false}
      propertyId={paymentRequest.requestPropertyId}
      renderAlways={(property?: IProperty) =>
        renderPaymentRequestAndProperty(paymentRequest, property)
      }
    />
  );

  const renderIbans = () => {
    if (ibans.length === 0) {
      if (isFetchingIbans || fetchIbanError) {
        return null;
      }

      return (
        <>
          <S.ListItemSelectWrap>
            <FormSectionSubtitle
              title={getLocalizedText(
                "payment_bank_account_list.section_header",
              )}
            />
          </S.ListItemSelectWrap>
          <S.ButtonPrimaryWrap>
            <ButtonPrimary
              title={getLocalizedText(
                "payment_bank_account_list.cta.add_bank_account",
              )}
              isInverted={true}
              onPress={handlePressAddIban}
            />
          </S.ButtonPrimaryWrap>
        </>
      );
    }

    return (
      <>
        <S.ListItemSelectWrap>
          <FormSectionSubtitle
            title={getLocalizedText("payment_bank_account_list.section_header")}
          />
        </S.ListItemSelectWrap>
        {ibans?.map(iban => {
          return (
            <React.Fragment key={iban.id}>
              <S.ListItemSelectWrap selected={selectedIbanId === iban.id}>
                <ListItemSelect
                  title={iban.iban}
                  subtitle={iban.ownerName}
                  extraSubtitle={
                    <VopStatusBadge status={iban.mangoVopStatus} />
                  }
                  selected={selectedIbanId === iban.id}
                  onPress={handlePressIban(iban)}
                  showSelectionLeft={true}
                />
              </S.ListItemSelectWrap>
              <ListItemSeparator />
            </React.Fragment>
          );
        })}
        <Spacer weight={ESpacerWeight.W16} />
        <S.ListItemSelectWrap>
          <S.ButtonBasicWrap>
            <ButtonBasic
              title={getLocalizedText(
                "payment_bank_account_list.cta.add_bank_account",
              )}
              onPress={handlePressAddIban}
            />
          </S.ButtonBasicWrap>
        </S.ListItemSelectWrap>
      </>
    );
  };

  const renderLoading = () => (
    <Page withScroll={false}>
      <FullScreenActivityIndicator />
    </Page>
  );

  const renderError = (retry: () => void, error?: Error) => (
    <Page withScroll={false}>
      <FullScreenErrorState
        showIcon={false}
        error={error}
        title={getLocalizedText("general.error")}
        actionTitle={getLocalizedText("try.again")}
        onPressAction={retry}
      />
    </Page>
  );

  if (!paymentRequestId) {
    return null;
  }

  return (
    <>
      <PaymentRequestRedirect id={paymentRequestId} />
      <PaymentRequestFetch
        id={paymentRequestId}
        renderPaymentRequest={renderPaymentRequest}
        renderLoading={renderLoading}
        renderError={renderError}
        extraData={{
          selectedIbanId,
          pendingPressPay,
        }}
        paymentRequestV2Enabled={true}
      />
    </>
  );
};

export const MangopayOverview = withKycRequired(MangopayOverviewBase);
